import React from 'react';
import { Input } from 'components/ui/input';
import { Download } from 'lucide-react';
import { Combobox } from 'components/ui/combobox';
import Dropdowns from 'components/Dropdowns';
import { useBreadcrumbs } from 'contexts/BreadcrumbsContext';
import {
  fetchPendingPodCustomers,
  fetchPendingPodData,
  fetchPendingPodVendors,
  PendingPodLevel,
  PendingPodLookupOption,
  PendingPodRow,
  updatePendingPodDropdowns,
  downloadPendingPods,
  PendingPodStatus
} from './api';
import PendingPODTable from './components/PendingPODTable';

type SortDirection = 'asc' | 'desc';

type ComboboxOption = {
  value: string;
  label: string;
};

const statusOptions: ComboboxOption[] = [
  {
    value: 'not_due',
    label: 'Not Due',
  },
  {
    value: 'overdue',
    label: 'Overdue',
  },
];

const PendingPodReportPage: React.FC = () => {
  const { setBreadcrumbs } = useBreadcrumbs();
  const [levels, setLevels] = React.useState<PendingPodLevel[]>([]);
  const [dropdownLoading, setDropdownLoading] = React.useState<boolean>(false);
  const [dropdownError, setDropdownError] = React.useState<string>('');
  const [lookupLoading, setLookupLoading] = React.useState<boolean>(false);
  const [customerOptions, setCustomerOptions] = React.useState<ComboboxOption[]>([]);
  const [vendorOptions, setVendorOptions] = React.useState<ComboboxOption[]>([]);
  const [search, setSearch] = React.useState<string>('');
  const [debouncedSearch, setDebouncedSearch] = React.useState<string>('');
  const [selectedCustomer, setSelectedCustomer] = React.useState<string>('');
  const [selectedVendor, setSelectedVendor] = React.useState<string>('');
  const [selectedStatus, setSelectedStatus] = React.useState<PendingPodStatus | ''>('');
  const [tableRows, setTableRows] = React.useState<PendingPodRow[]>([]);
  const [tableLoading, setTableLoading] = React.useState<boolean>(false);
  const [tableError, setTableError] = React.useState<string>('');
  const [downloadingExcel, setDownloadingExcel] = React.useState<boolean>(false);
  const [page, setPage] = React.useState<number>(1);
  const [perPage, setPerPage] = React.useState<number>(20);
  const [totalItems, setTotalItems] = React.useState<number>(0);
  const [sortBy, setSortBy] = React.useState<keyof PendingPodRow>('cn_number');
  const [sortOrder, setSortOrder] = React.useState<SortDirection>('asc');

  const buildFilters = React.useCallback((currentLevels: PendingPodLevel[]) => {
    const filters: Record<string, string[]> = {};
    currentLevels.forEach((level) => {
      if (level.selected && level.selected.length > 0) {
        filters[String(level.id)] = level.selected;
      }
    });
    return filters;
  }, []);

  const selectedFiltersKey = React.useMemo(() => JSON.stringify(buildFilters(levels)), [buildFilters, levels]);
  const selectedFilters = React.useMemo(
    () => JSON.parse(selectedFiltersKey || '{}') as Record<string, string[]>,
    [selectedFiltersKey]
  );

  const fetchDropdowns = React.useCallback(async (selection: Record<string, string[]> = {}) => {
    setDropdownLoading(true);
    setDropdownError('');
    try {
      const resp = await updatePendingPodDropdowns({ selection });
      setLevels(Array.isArray(resp?.levels) ? resp.levels : []);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to load filters';
      setDropdownError(message);
      setLevels([]);
    } finally {
      setDropdownLoading(false);
    }
  }, []);

  const fetchCustomers = React.useCallback(async (selection: Record<string, string[]> = {}, vendorCode?: string) => {
      try {
        const resp = await fetchPendingPodCustomers({
          filters: selection,
          vendor_code: vendorCode,
        });

        const options = (resp?.items ?? []).map((item) => ({
          value: item.code,
          label: item.name,
        }));
        setCustomerOptions(options);
        // clear a selection that's no longer in the refetched list
        const codes = new Set(options.map((o) => o.value));
        setSelectedCustomer((prev) => (prev && !codes.has(prev) ? '' : prev));
      } catch {
        setCustomerOptions([]);
      }
    },
    []
  );

  const fetchVendors = React.useCallback(async (selection: Record<string, string[]> = {}, custCode?: string) => {
      try {
        const resp = await fetchPendingPodVendors({
          filters: selection,
          cust_code: custCode,
        });

        const options = (resp?.items ?? []).map((item) => ({
          value: item.code,
          label: item.name,
        }));
        setVendorOptions(options);
        // clear a selection that's no longer in the refetched list
        const codes = new Set(options.map((o) => o.value));
        setSelectedVendor((prev) => (prev && !codes.has(prev) ? '' : prev));
      } catch {
        setVendorOptions([]);
      }
    }, []);

  const fetchTableData = React.useCallback(async (
    currentFilters: Record<string, string[]>,
    pageOverride?: number,
    sortOverride?: keyof PendingPodRow,
    orderOverride?: SortDirection,
    perPageOverride?: number,
  ) => {
    setTableLoading(true);
    setTableError('');
    try {
      const resp = await fetchPendingPodData({
        filters: currentFilters,
        search: debouncedSearch.trim() || undefined,
        cust_code: selectedCustomer || undefined,
        vendor_code: selectedVendor || undefined,
        status: selectedStatus || undefined,
        page: pageOverride ?? page,
        per_page: perPageOverride ?? perPage,
        sort_by: String(sortOverride ?? sortBy),
        sort_order: orderOverride ?? sortOrder,
      });

      setTableRows(Array.isArray(resp?.rows) ? resp.rows : []);
      setTotalItems(Number(resp?.total_items || 0));
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to fetch report data';
      setTableError(message);
      setTableRows([]);
      setTotalItems(0);
    } finally {
      setTableLoading(false);
    }
  }, [debouncedSearch, page, perPage, selectedCustomer, selectedVendor, selectedStatus, sortBy, sortOrder]);

  const handleDownloadExcel = React.useCallback(async () => {
    try {
      setDownloadingExcel(true);
      setTableError('');

      const blob = await downloadPendingPods({
        filters: selectedFilters,
        search: debouncedSearch.trim() || undefined,
        cust_code: selectedCustomer || undefined,
        vendor_code: selectedVendor || undefined,
        status: selectedStatus || undefined,
        sort_by: String(sortBy),
        sort_order: sortOrder,
      });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'pending_pods.xlsx';
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to download Excel';
      setTableError(message);
    } finally {
      setDownloadingExcel(false);
    }
  }, [selectedFilters, debouncedSearch, selectedCustomer, selectedVendor, selectedStatus, sortBy, sortOrder]);

  React.useEffect(() => {
    setBreadcrumbs([
      { label: 'Reports', href: '/reports/pnl' },
      { label: 'Pending PODs' },
    ]);
  }, [setBreadcrumbs]);

  React.useEffect(() => {
    // initial load
    setPage(1);
    setLevels([]);
    setSearch('');
    setDebouncedSearch('');
    setSelectedCustomer('');
    setSelectedVendor('');
    setSelectedStatus('');
    fetchDropdowns({});
    setLookupLoading(true);
      Promise.all([
        fetchCustomers({}),
        fetchVendors({}),
      ]).finally(() => setLookupLoading(false));
  }, [fetchDropdowns, fetchCustomers, fetchVendors]);

  React.useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
    }, 400);

    return () => window.clearTimeout(timer);
  }, [search]);

  React.useEffect(() => {
    fetchTableData(selectedFilters, page, sortBy, sortOrder, perPage);
  }, [debouncedSearch, selectedCustomer, selectedVendor, selectedStatus, selectedFilters, page, perPage, sortBy, sortOrder, fetchTableData]);

  const handleSelectChange = React.useCallback((levelId: number | string, value: string[]) => {
    // changing a level clears all lower (descendant) selections (mirrors PnlPage)
    const desc = (levels || [])
      .filter((l) => Boolean(l?.visible))
      .sort((a, b) => Number(b.id) - Number(a.id));
    const indexById = Object.fromEntries(desc.map((l, i) => [String(l.id), i]));
    const changedIdx = indexById[String(levelId)];

    const currentLevels = levels.map((level) => {
      const idx = indexById[String(level.id)];
      if (idx === changedIdx) {
        return { ...level, selected: value.length > 0 ? value : null };
      }
      if (idx > changedIdx) {
        return { ...level, selected: null };
      }
      return level;
    });

    setLevels(currentLevels);
    const selection = buildFilters(currentLevels);
    fetchDropdowns(selection);

    setLookupLoading(true);
    Promise.all([
      fetchCustomers(selection, selectedVendor || undefined),
      fetchVendors(selection, selectedCustomer || undefined),
    ]).finally(() => setLookupLoading(false));
    setPage(1);
  }, [buildFilters, fetchDropdowns, fetchCustomers, fetchVendors, selectedCustomer, selectedVendor, levels]);

  const toggleSort = React.useCallback((column: keyof PendingPodRow) => {
    const nextOrder: SortDirection = sortBy === column && sortOrder === 'asc' ? 'desc' : 'asc';
    setSortBy(column);
    setSortOrder(nextOrder);
    setPage(1);
  }, [sortBy, sortOrder]);

  const handlePageChange = React.useCallback((nextPage: number) => {
    const safePage = Math.max(1, nextPage);
    setPage(safePage);
  }, []);
  return (
    <div className="mx-auto flex h-full min-h-0 w-full max-w-screen-2xl flex-col gap-6 px-4 sm:px-6 2xl:max-w-[2000px]">
      <div className="shrink-0 space-y-4">
        <Dropdowns
          showYear={false}
          levels={levels}
          loading={dropdownLoading}
          onSelectChange={handleSelectChange}
        />

        {dropdownError && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-md">
            <p className="text-sm text-red-600">{dropdownError}</p>
          </div>
        )}
      </div>

      <div className="shrink-0 grid gap-4 lg:grid-cols-[1fr_1fr_1fr_1fr_auto] items-end">
        <div className="space-y-1">
          <label className="text-sm font-medium text-gray-700">Search CN No.</label>
          <Input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Type CN number..."
          />
        </div>

        <div className="space-y-1">
          <label className="text-sm font-medium text-gray-700">Customer</label>
          <Combobox
            options={customerOptions}
            value={selectedCustomer}
            onValueChange={(value) => {
              setSelectedCustomer(value);
              setLookupLoading(true);
              fetchVendors(selectedFilters, value)
                .finally(() => setLookupLoading(false));
              setPage(1);
            }}
            placeholder={lookupLoading ? 'Loading customers...' : 'All customers'}
            searchPlaceholder="Search customer..."
            emptyText="No customer found."
            disabled={lookupLoading}
          />
        </div>

        <div className="space-y-1">
          <label className="text-sm font-medium text-gray-700">Vendor</label>
          <Combobox
            options={vendorOptions}
            value={selectedVendor}
            onValueChange={(value) => {
              setSelectedVendor(value);
              setLookupLoading(true);
              fetchCustomers(selectedFilters, value)
                .finally(() => setLookupLoading(false));
              setPage(1);
            }}
            placeholder={lookupLoading ? 'Loading vendors...' : 'All vendors'}
            searchPlaceholder="Search vendor..."
            emptyText="No vendor found."
            disabled={lookupLoading}
          />
        </div>

        <div className="space-y-1">
          <label className="text-sm font-medium text-gray-700">Status</label>
          <Combobox
            options={statusOptions}
            value={selectedStatus}
            onValueChange={(value) => {
              setSelectedStatus(value as PendingPodStatus | '');
              setPage(1);
            }}
            placeholder="All Statuses"
            searchPlaceholder="Search status..."
            emptyText="No status found."
          />
        </div>

        <div className="flex justify-end">
          <button
            className="px-3 py-1 text-sm rounded-md border border-green-600 text-green-700 hover:bg-green-50 transition-colors flex items-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed"
            onClick={handleDownloadExcel}
            disabled={downloadingExcel}
          >
            <Download size={16} />
            {downloadingExcel ? 'Downloading...' : 'Excel'}
          </button>
        </div>
      </div>

      {tableError && (
        <div className="shrink-0 rounded-md border border-red-200 bg-red-50 p-3">
          <p className="text-sm text-red-600">{tableError}</p>
        </div>
      )}

      <div className="min-h-0 flex-1">
        <PendingPODTable
          data={tableRows}
          loading={tableLoading}
          page={page}
          pageSize={perPage}
          totalItems={totalItems}
          sortBy={sortBy}
          sortOrder={sortOrder}
          onSortChange={toggleSort}
          onPageChange={handlePageChange}
          onPageSizeChange={(size) => {
            setPerPage(size);
            setPage(1);
          }}
        />
      </div>
    </div>
  );
};

export default PendingPodReportPage;
